import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// UTILS
import { intercom } from 'utils';

// SERVICES
import { FleetSettings } from 'services/resources';
import { selectLoading, selectErrors, selectFleetSettings } from 'services/FleetSettings/selectors';
import { getFleetSettings, updateSettings } from 'services/FleetSettings/thunks';
import { selectSelectedHierarchyEntity } from 'services/Hierarchy/selectors';

// COMPONENTS
import FlashMessage from 'components/FlashMessage';
import { SaveButton } from 'components/Buttons';
import { CanOrNotFound } from 'permissions';
import ErrorMessages from 'components/ErrorMessages';
import { ToggleInput } from 'components/FormField';
import Alerts from '../Alerts';

// HOOKS
import { useAppDispatch } from 'hooks/store';
import useStrategy, { FeatureFlags } from 'hooks/useStrategy';

// STYLES
import { SettingsPageContent } from 'styles/settings';
import { Label, Input } from 'styles/form';
import { Section, SectionHeader, SectionContent, SectionHeaderTitle, SectionHeaderActions } from 'styles/cards';
import { SubSection, InputContainer, InputIndicator, Divider } from '../FleetSettings.styles';

// CONSTANTS
import { FLASH_MESSAGE_TYPES, UserPermission, FleetSettingsSideMenuItem, DotAppRoute } from 'constants/common';
import colors from 'styles/colors';

// ASSETS
import LegalIcon from 'assets/LegalIcon';
import styled from 'styled-components';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AgreementSettings = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const fleetSettings = useSelector(selectFleetSettings());
  const selectedHierarchyEntity = useSelector(selectSelectedHierarchyEntity());
  const loading = useSelector(selectLoading());
  const errors = useSelector(selectErrors());

  const [flashMessageVisible, setFlashMessageVisible] = useState(false);
  const [flashMessageType, setFlashMessageType] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);

  const requireCheckoutSignatureEnabled = useStrategy(FeatureFlags.REQUIRE_CHECKOUT_SIGNATURE);

  useEffect(() => {
    dispatch(getFleetSettings());
  }, [dispatch, selectedHierarchyEntity]);

  const handleOpenFlashMessage = (text, type) => {
    setFlashMessageVisible(true);
    setFlashMessageType(type);
    setFlashMessage(text);
  };

  const handleCloseFlashMessage = () => {
    setFlashMessageVisible(false);
    setFlashMessageType(null);
    setFlashMessage(null);
  };

  const handleSave = (values) => {
    return dispatch(
      updateSettings(fleetSettings.get('id'), {
        defaultDayOut: Number(values.defaultDayOut),
        twoSignatureEnabled: values.twoSignatureEnabled,
        requireInsuranceOnAgreement: values.requireInsuranceOnAgreement,
        requireCreditCardOnAgreement: values.requireCreditCardOnAgreement,
        requireCheckoutSignature: values.requireCheckoutSignature,
      })
    )
      .then(() => {
        intercom.trackEvent('Updated Fleet settings', {});
        handleOpenFlashMessage(
          intl.formatMessage({ id: 'views.FleetSettings.Agreement.settingsUpdated' }),
          FLASH_MESSAGE_TYPES.success
        );
      })
      .catch(() => {
        handleOpenFlashMessage(
          intl.formatMessage({ id: 'views.FleetSettings.Agreement.settingsCouldNotBeUpdated' }),
          FLASH_MESSAGE_TYPES.error
        );
      });
  };

  const enableSave = (values: Partial<FleetSettings>, initialValues: Partial<FleetSettings>) => {
    return (
      values.defaultDayOut === initialValues.defaultDayOut &&
      values.twoSignatureEnabled === initialValues.twoSignatureEnabled &&
      values.requireInsuranceOnAgreement === initialValues.requireInsuranceOnAgreement &&
      values.requireCreditCardOnAgreement === initialValues.requireCreditCardOnAgreement &&
      values.requireCheckoutSignature === initialValues.requireCheckoutSignature
    );
  };

  const initialValues = {
    defaultDayOut: fleetSettings.get('defaultDayOut'),
    twoSignatureEnabled: fleetSettings.get('twoSignatureEnabled'),
    requireInsuranceOnAgreement: fleetSettings.get('requireInsuranceOnAgreement'),
    requireCreditCardOnAgreement: fleetSettings.get('requireCreditCardOnAgreement'),
    requireCheckoutSignature: fleetSettings.get('requireCheckoutSignature'),
  };

  return (
    <CanOrNotFound I={UserPermission.VIEW} a={DotAppRoute.SETTINGS} field={FleetSettingsSideMenuItem.AGREEMENT}>
      <SettingsPageContent>
        <Helmet>
          <title>{intl.formatMessage({ id: 'views.Settings.helmetTitle' })}</title>
          <meta name="settings" content={intl.formatMessage({ id: 'views.Settings.helmetDescription' })} />
        </Helmet>

        <FlashMessage
          visible={flashMessageVisible}
          type={flashMessageType}
          text={flashMessage}
          onClose={handleCloseFlashMessage}
        />

        <Formik initialValues={initialValues} onSubmit={handleSave} enableReinitialize>
          {({ isSubmitting, values }) => (
            <Form>
              <Section>
                <SectionHeader>
                  <SectionHeaderTitle>
                    <LegalIcon color={colors.fleetBlue} />
                    <FormattedMessage id="views.Agreement.title" />
                  </SectionHeaderTitle>
                  <SectionHeaderActions>
                    <SaveButton
                      loading={loading || isSubmitting}
                      disabled={enableSave(values, initialValues)}
                      type="submit"
                    />
                  </SectionHeaderActions>
                </SectionHeader>
                <SectionContent>
                  <ContentWrapper>
                    <div>
                      <Label>
                        <FormattedMessage id="views.FleetSettings.Alerts.defaultDaysOutLabel" />
                      </Label>
                      <SubSection>
                        <FormattedMessage id="views.FleetSettings.Alerts.defaultDaysOutDescription" />
                      </SubSection>
                      <InputContainer>
                        <Field
                          name="defaultDayOut"
                          as={Input}
                          type="number"
                          placeholder={intl.formatMessage({
                            id: 'views.FleetSettings.Alerts.placeholder',
                          })}
                        />
                        <InputIndicator>
                          <FormattedMessage id="views.FleetSettings.Alerts.days" />
                        </InputIndicator>
                      </InputContainer>
                      {/* @ts-expect-error  Errors are currently untyped */}
                      <ErrorMessages errors={errors.get('defaultDayOut')} />
                    </div>
                    <Divider />
                    <div>
                      <Label>
                        <FormattedMessage id="views.FleetSettings.Agreement.twoSignaturesLabel" />
                      </Label>
                      <SubSection>
                        <FormattedMessage id="views.FleetSettings.Agreement.twoSignaturesDescription" />
                      </SubSection>
                      <InputContainer>
                        <Field
                          as={ToggleInput}
                          type="checkbox"
                          name="twoSignatureEnabled"
                          inlineLabel={intl.formatMessage({
                            id: 'views.FleetSettings.Agreement.enableTwoSignatures',
                          })}
                          style={{ marginLeft: '10px' }}
                        />
                      </InputContainer>
                      {/* @ts-expect-error  Errors are currently untyped */}
                      <ErrorMessages errors={errors.get('twoSignatureEnabled')} />
                    </div>
                    <Divider />
                    <div>
                      <Label>
                        <FormattedMessage id="views.FleetSettings.Agreement.requireInsuranceDetails" />
                      </Label>
                      <SubSection>
                        <FormattedMessage id="views.FleetSettings.Agreement.requireInsuranceDetailsDescription" />
                      </SubSection>
                      <InputContainer>
                        <Field
                          as={ToggleInput}
                          type="checkbox"
                          name="requireInsuranceOnAgreement"
                          inlineLabel={intl.formatMessage({
                            id: 'views.FleetSettings.Agreement.enableInsuranceRequirement',
                          })}
                          style={{ marginLeft: '10px' }}
                        />
                      </InputContainer>
                      {/* @ts-expect-error  Errors are currently untyped */}
                      <ErrorMessages errors={errors.get('requireInsuranceOnAgreement')} />
                    </div>
                    <Divider />
                    <div>
                      <Label>
                        <FormattedMessage id="views.FleetSettings.Agreement.requireCreditCardDetails" />
                      </Label>
                      <SubSection>
                        <FormattedMessage id="views.FleetSettings.Agreement.requireCreditCardDetailsDescription" />
                      </SubSection>
                      <InputContainer>
                        <Field
                          as={ToggleInput}
                          type="checkbox"
                          name="requireCreditCardOnAgreement"
                          inlineLabel={intl.formatMessage({
                            id: 'views.FleetSettings.Agreement.enableCreditCardRequirement',
                          })}
                          style={{ marginLeft: '10px' }}
                        />
                      </InputContainer>
                      {/* @ts-expect-error  Errors are currently untyped */}
                      <ErrorMessages errors={errors.get('requireCreditCardOnAgreement')} />
                    </div>
                    {requireCheckoutSignatureEnabled && (
                      <>
                        <Divider />
                        <div>
                          <Label>
                            <FormattedMessage id="views.FleetSettings.Agreement.requireCheckoutSignature" />
                          </Label>
                          <SubSection>
                            <FormattedMessage id="views.FleetSettings.Agreement.requireCheckoutSignatureDescription" />
                          </SubSection>
                          <InputContainer>
                            <Field
                              as={ToggleInput}
                              type="checkbox"
                              name="requireCheckoutSignature"
                              inlineLabel={intl.formatMessage({
                                id: 'views.FleetSettings.Agreement.enableCheckoutSignatureRequirement',
                              })}
                              style={{ marginLeft: '10px' }}
                            />
                          </InputContainer>
                          {/* @ts-expect-error  Errors are currently untyped */}
                          <ErrorMessages errors={errors.get('requireCheckoutSignature')} />
                        </div>
                      </>
                    )}
                  </ContentWrapper>
                </SectionContent>
              </Section>
            </Form>
          )}
        </Formik>
      </SettingsPageContent>
    </CanOrNotFound>
  );
};

export default AgreementSettings;
